import React from "react";
import { Button } from "components/DesignSystem/Button/Button";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Files } from "components/icons/Files";
import classNames from "classnames";
import dayjs from "dayjs";

export const DeadlineItemSkeleton = () => (
  <div className="t-flex t-items-center t-gap-3 t-py-3 t-px-5 t-border-0 t-border-b t-border-solid t-border-neutral-10 last:t-border-b-0">
    <div className="t-flex-1 t-flex t-flex-col t-gap-1.5">
      <Skeleton className="t-h-4 t-w-40" />
      <Skeleton className="t-h-3 t-w-24" />
    </div>
    <div className="t-flex t-items-center t-gap-3 t-flex-shrink-0">
      <Skeleton className="t-h-3 t-w-16" />
      <Skeleton className="t-h-8 t-w-20" />
    </div>
  </div>
);

const DASHBOARD_STATUS_TAG: Record<
  string,
  { label: string; tagType: "green" | "yellow" | "red" | "blue" | "gray" }
> = {
  COMPLETED: { label: "Completed", tagType: "green" },
  ONGOING: { label: "In progress", tagType: "yellow" },
  ACTION_REQUIRED: { label: "Action required", tagType: "red" },
  PAYMENT_PENDING: { label: "Payment pending", tagType: "blue" },
  UPCOMING_DEADLINES: { label: "Upcoming", tagType: "gray" },
  OVERDUE: { label: "Overdue", tagType: "red" },
};

type DeadlineItemProps = {
  deadlineDate: string | null;
  title: string;
  sub?: string;
  dashboardStatus: string;
  actionLabel?: string;
  onAction?: () => void;
  isUrgent: boolean;
  isWarn: boolean;
  isLoading?: boolean;
};

export const DeadlineItem = ({
  deadlineDate,
  title,
  sub,
  dashboardStatus,
  actionLabel,
  onAction,
  isUrgent,
  isWarn,
  isLoading,
}: DeadlineItemProps) => {
  const statusConfig = DASHBOARD_STATUS_TAG[dashboardStatus];
  const daysLeft = deadlineDate
    ? dayjs(deadlineDate).diff(dayjs(), "day")
    : null;
  const formattedDate = deadlineDate
    ? dayjs(deadlineDate).format("MMM DD, YYYY")
    : null;

  return (
    <div className="t-flex t-items-center t-gap-3 t-py-3 t-px-5 t-border-0 t-border-b t-border-solid t-border-neutral-10 last:t-border-b-0 sm:t-flex-wrap sm:t-gap-2 sm:t-px-4">
      <div className="t-flex-1 t-min-w-0 sm:t-basis-full">
        <div className="t-text-subtext t-font-medium t-text-text-100 t-truncate sm:t-whitespace-normal">
          {title}
        </div>
        {sub && (
          <div className="t-text-body-sm t-font-light t-text-text-30 t-mt-0.5 t-truncate">
            {sub}
          </div>
        )}
      </div>

      <div className="t-flex t-items-center t-gap-3 t-flex-shrink-0 sm:t-w-full sm:t-justify-between">
        {statusConfig && (
          <div className="t-flex t-flex-col t-items-center t-gap-0.5 sm:t-items-start">
            <Tag tagType={statusConfig.tagType} size="small" icon={false}>
              {statusConfig.label}
            </Tag>
            {formattedDate && (
              <div className="t-text-caption t-text-text-30">
                {formattedDate}
              </div>
            )}
          </div>
        )}
        {!statusConfig && formattedDate && (
          <div className="t-text-right sm:t-text-left">
            <div
              className={classNames("t-text-body-sm t-font-medium", {
                "t-text-red-50": isUrgent,
                "t-text-yellow-60": isWarn && !isUrgent,
                "t-text-text-100": !isUrgent && !isWarn,
              })}
            >
              {formattedDate}
            </div>
            {daysLeft !== null && daysLeft >= 0 && (
              <div className="t-text-body-sm t-font-light t-text-text-30">
                in {daysLeft} days
              </div>
            )}
          </div>
        )}
        {actionLabel && onAction && (
          <Button
            size="small"
            customType="secondary"
            onClick={onAction}
            isLoading={isLoading}
            disabled={isLoading}
          >
            {actionLabel}
          </Button>
        )}
      </div>
    </div>
  );
};
